import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { ROTATION_OPEN_STATUSES, WEIGHBRIDGE_TIMING_LABELS, fmtKg } from '@/lib/missions';
import { useHasPermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RotationDialog } from '@/components/missions/RotationDialog';

import type { Mission, MissionRotation } from '@logistech/ui-shared';

const kg = (v: string | number | null) => (v != null ? fmtKg(Number(v)) : '—');

export function RotationsTab({ mission }: { mission: Mission }) {
  const qc = useQueryClient();
  const canWrite = useHasPermission('missions.write');
  const canDelete = useHasPermission('rotations.delete');
  const [dialog, setDialog] = useState<{ open: boolean; rotation?: MissionRotation }>({ open: false });

  const rotations = [...(mission.rotations ?? [])].sort((a, b) => a.sequence - b.sequence);
  const count = mission.aggregates?.totalRotations ?? rotations.length;
  const target = mission.targetRotations;
  const reached = mission.aggregates?.objectiveReached ?? false;
  const totalNetKg = mission.aggregates?.totalNetKg ?? 0;
  // L'API refuse toute saisie hors SCHEDULED / IN_PROGRESS (brouillon compris)
  const open = ROTATION_OPEN_STATUSES.includes(mission.status);

  const remove = useMutation({
    mutationFn: (rotationId: string) => api.delete(`/missions/${mission.id}/rotations/${rotationId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mission', mission.id] });
      void qc.invalidateQueries({ queryKey: ['missions'] });
      toast.success('Rotation supprimée');
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors de la suppression')),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="flex items-center gap-4">
            <span
              className={cn(
                'rounded-full px-4 py-1 text-lg font-bold text-white',
                target == null ? 'bg-muted-foreground' : reached ? 'bg-success' : 'bg-warning',
              )}
            >
              {count} / {target ?? '—'} rotations
            </span>
            <span className="text-sm text-muted-foreground">
              Total net : <span className="font-semibold text-foreground">{(totalNetKg / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 3 })} t</span>
              {' '}({fmtKg(totalNetKg)})
            </span>
            {canWrite && open && (
              <Button className="ml-auto" onClick={() => setDialog({ open: true })}>
                <Plus className="h-4 w-4 mr-2" /> Ajouter une rotation
              </Button>
            )}
          </div>
          {target != null && (
            <Progress value={(count / target) * 100} indicatorClassName={reached ? 'bg-success' : 'bg-warning'} />
          )}
          {mission.status === 'DRAFT' && (
            <p className="text-xs text-muted-foreground">Les rotations se saisissent une fois la mission planifiée.</p>
          )}
        </CardContent>
      </Card>

      <div className="bg-card rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Date/heure</TableHead>
              <TableHead>Type bascule</TableHead>
              <TableHead className="text-right">Brut</TableHead>
              <TableHead className="text-right">Tare</TableHead>
              <TableHead className="text-right">Net</TableHead>
              <TableHead>Ticket</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rotations.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono">{r.sequence}</TableCell>
                <TableCell>{new Date(r.weighedAt ?? r.createdAt).toLocaleString('fr-FR')}</TableCell>
                <TableCell>{r.weighbridgeTiming ? WEIGHBRIDGE_TIMING_LABELS[r.weighbridgeTiming] : '—'}</TableCell>
                <TableCell className="text-right">{kg(r.grossWeightKg)}</TableCell>
                <TableCell className="text-right">{kg(r.tareWeightKg)}</TableCell>
                <TableCell className="text-right font-semibold">{kg(r.netWeightKg)}</TableCell>
                <TableCell>{r.ticketNumber ?? '—'}</TableCell>
                <TableCell className="space-x-1 whitespace-nowrap">
                  {canWrite && open && (
                    <Button variant="ghost" size="sm" title="Modifier" onClick={() => setDialog({ open: true, rotation: r })}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  {canDelete && open && (
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Supprimer"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(`Supprimer la rotation n° ${r.sequence} ? Les suivantes seront renumérotées.`)) remove.mutate(r.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-danger" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rotations.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  Aucune rotation enregistrée. Cliquez sur « Ajouter une rotation » pour commencer.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <RotationDialog
        missionId={mission.id}
        vehicleTareKg={mission.vehicle?.tareWeightKg}
        rotation={dialog.rotation}
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
      />
    </div>
  );
}
